import { formatDate, formatMoney } from '@betterment/core';
import { Box, Button, Dialog, H1, Icon, ListRow, Separator, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';
import { ParentSize } from '@visx/responsive';

import { ProjectionLoadingState } from './loading-state/ProjectionLoadingState';

import { ProjectionChart } from '../../../../../../Shared/components-v2/charts/projection-chart/ProjectionChart';
import { LoadingAccountProjectionChart } from '../../../../../../Shared/components-v2/loading-Account-projection-chart/LoadingAccountProjectionChart';
import { MinimalFeeWarning } from '../../../../../../Shared/components-v2/minimal-fee-warning/MinimalFeeWarning';
import { urlBuilder } from '../../../../../../Shared/urlBuilder';
import { useEnvelopeAccountProjections } from '../../hooks/useEnvelopeAccountProjections';

export const ProjectionChartSection = () => {
  const { data } = useEnvelopeAccountProjections();
  const accountId = useRequiredParam('accountId');

  if (!data) return <ProjectionLoadingState />;
  if (!('projection' in data.account)) return null;

  const { projectionReturnsDisclosure, projectionReturnsAssumptionsDisclosure } = data.disclosures;

  const timeSeries = data.account.projection?.timeSeries;

  if (!timeSeries?.length) return null;

  const isZeroState = !data.account.hasEverBeenFunded;
  const lastProjectedBalance = timeSeries[timeSeries.length - 1];

  return (
    <Box css={{ minWidth: '0px' }}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.300' }}>
        {data.account.minFeeWarning && <MinimalFeeWarning minFeeWarning={data.account.minFeeWarning} />}
        <Box css={{ display: 'flex', flexDirection: 'column' }}>
          <Text variant="text100">Projected {formatDate(lastProjectedBalance.date, 'longOrdinalShortMonth')}</Text>
          <Text variant="text400">{formatMoney(lastProjectedBalance.expected)}</Text>
        </Box>

        <ParentSize>
          {({ width }) =>
            isZeroState ? (
              <LoadingAccountProjectionChart variant="zero-state" width={width} height={200} />
            ) : (
              <ProjectionChart
                data={timeSeries}
                width={width}
                height={220}
                margin={{ top: 5, right: 0, bottom: 20, left: 42 }}
              />
            )
          }
        </ParentSize>

        <Box>
          <Separator />
          <SambaAutoLink
            to={urlBuilder.appAccountForecaster.absolute({ accountId })}
            data-samba-component={true}
            css={{ color: 'text' }}
          >
            <ListRow css={{ padding: 'token(spacing.space.300) token(spacing.space.0)' }}>
              <ListRow.LeadingAsset>
                <Icon name="Guidance" size="small" color="icon.accent.gold" />
              </ListRow.LeadingAsset>
              <ListRow.LeadingContent>
                <H1 variant="title100">Forecaster</H1>
                <Text variant="text50">See how your money could grow with our projection tool.</Text>
              </ListRow.LeadingContent>
              <ListRow.TrailingAsset>
                <Icon name="CaretRight" size="small" />
              </ListRow.TrailingAsset>
            </ListRow>
            <Separator />
          </SambaAutoLink>
        </Box>
        <Text variant="text25" color="text.weak">
          {projectionReturnsDisclosure}{' '}
          <Dialog overlay="default">
            <Dialog.Trigger data-samba-component={true} asChild={true}>
              <Button variant="textSecondary" css={{ textStyle: 'text.50' }}>
                Learn more
              </Button>
            </Dialog.Trigger>
            <Dialog.Content
              css={{
                padding: 'space.400',
                width: '100%',
                maxWidth: 'container.xlarge',
                maxHeight: '80vh',
                overflow: 'auto',
              }}
            >
              <Text
                color="text.placeholder"
                css={{
                  '& p': {
                    fontSize: 'fontSize.50',
                    lineHeight: 'lineHeight.50',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: projectionReturnsAssumptionsDisclosure }}
              />
            </Dialog.Content>
          </Dialog>
        </Text>
      </Box>
    </Box>
  );
};
